import { ApiService } from './api.service';
import type {
  Component,
  CreateComponentDto,
  UpdateComponentDto,
} from '@risk-management/shared';

// Threat profile types from backend
export interface CountermeasureInfo {
  id: string;
  name: string;
  description: string;
  url: string;
  tactic: string;
}

export interface SubtechniqueInfo {
  id: string;
  name: string;
  description: string;
  url: string;
  tactics: string[];
  countermeasures: CountermeasureInfo[];
}

export interface ThreatTemplate {
  id: string;
  title: string;
  description: string;
  threatSource?: string;
  vulnerability?: string;
  attackTechniqueId: string;
  attackTechniqueName: string;
  attackTechniqueUrl: string;
  subtechniqueCount: number;
  ciaImpact?: {
    confidentiality: number;
    integrity: number;
    availability: number;
  };
  confidentialityImpact?: number;
  integrityImpact?: number;
  availabilityImpact?: number;
  likelihood: number;
  countermeasures: CountermeasureInfo[];
  suggestedMitigations: Array<{
    id?: string;
    title: string;
    description: string;
    d3fendId?: string;
    d3fendName?: string;
    d3fendUrl?: string;
    isoControlId?: string;
    reductionFactor: number;
    controlType: string;
    appliesTo?: string[];
  }>;
}

export interface ComponentThreatProfile {
  componentId: string;
  componentType: string;
  componentName: string;
  threats: ThreatTemplate[];
  additionalMitigations: Array<{
    title: string;
    description: string;
    d3fendId?: string;
    d3fendName?: string;
    d3fendUrl?: string;
    reductionFactor: number;
    controlType: string;
  }>;
  profileSource: string;
}

class ComponentsService extends ApiService {
  constructor() {
    super();
  }

  /**
   * Get all components for a specific space
   */
  async getBySpace(
    spaceId: string,
    includeGlobal: boolean = true
  ): Promise<Component[]> {
    const params = new globalThis.URLSearchParams();
    params.append('includeGlobal', String(includeGlobal));

    const queryString = params.toString();
    return this.get(
      `/spaces/${spaceId}/components${queryString ? `?${queryString}` : ''}`
    );
  }

  async getById(id: string): Promise<Component> {
    return this.get(`/components/${id}`);
  }

  async getByIds(ids: string[]): Promise<Component[]> {
    if (!ids || ids.length === 0) return [];
    try {
      // Use batch endpoint for efficiency
      const components = await this.post<Component[]>('/components/batch', {
        ids,
      });
      console.info(
        `[ComponentsService] Batch fetched ${components.length}/${ids.length} components`
      );
      return components;
    } catch (error) {
      console.error(
        '[ComponentsService] Batch fetch failed, falling back to individual requests:',
        error
      );
      // Fallback to individual requests
      const promises = ids.map(id =>
        this.getById(id).catch(err => {
          console.error(
            `[ComponentsService] Failed to fetch component ${id}:`,
            err
          );
          return null;
        })
      );
      const results = await Promise.all(promises);
      return results.filter((c): c is Component => c !== null);
    }
  }

  async create(data: CreateComponentDto): Promise<Component> {
    return this.post('/components', data);
  }

  async update(id: string, data: UpdateComponentDto): Promise<Component> {
    return this.patch(`/components/${id}`, data);
  }

  async transferToSpace(id: string, targetSpaceId: string): Promise<Component> {
    return this.post(`/components/${id}/transfer`, { targetSpaceId });
  }

  async deleteComponent(id: string): Promise<void> {
    await super.delete(`/components/${id}`);
  }

  /**
   * Get threat profile for a component based on its type
   * Returns ATT&CK threats and D3FEND countermeasures
   */
  async getThreatProfile(componentId: string): Promise<ComponentThreatProfile> {
    return this.get(`/components/${componentId}/threat-profile`);
  }

  /**
   * Get subtechniques for an ATT&CK technique
   * Returns subtechniques with their D3FEND countermeasures
   */
  async getSubtechniques(
    componentId: string,
    techniqueId: string
  ): Promise<SubtechniqueInfo[]> {
    return this.get(
      `/components/${componentId}/threat-profile/subtechniques/${techniqueId}`
    );
  }
}

export const componentsService = new ComponentsService();
